import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import i18n from "./i18n";
import { getStoredLanguage, setStoredLanguage } from "./languageStorage";

const LocalizationContext = createContext(null);

// IMPORTANT: Language selection should NOT change UI direction/alignment.
// We keep the app direction LTR regardless of chosen language.
const getDir = (_lang) => "ltr";

const applyDocumentLangDir = (lang) => {
    if (typeof document === "undefined") return;
    const dir = getDir(lang);
    try {
        document.documentElement.lang = lang;
        document.documentElement.dir = dir;
        document.body.dir = dir;
    } catch {
        // ignore
    }
};

export function LocalizationProvider({ children }) {
    const [language, setLanguageState] = useState(() => getStoredLanguage());

    // Ensure DOM + i18n match initial language immediately.
    useMemo(() => {
        applyDocumentLangDir(language);
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setLanguage = useCallback((nextLang) => {
        if (!nextLang || nextLang === language) return;
        setLanguageState(nextLang);
        setStoredLanguage(nextLang);
        applyDocumentLangDir(nextLang);
        i18n.changeLanguage(nextLang);
    }, [language]);

    const value = useMemo(() => {
        return {
            language,
            dir: getDir(language),
            setLanguage,
            isRTL: getDir(language) === "rtl",
        };
    }, [language, setLanguage]);

    return (
        <LocalizationContext.Provider value={value}>
            {children}
        </LocalizationContext.Provider>
    );
}

export function useLocalization() {
    const ctx = useContext(LocalizationContext);
    if (!ctx) {
        throw new Error("useLocalization must be used within LocalizationProvider");
    }
    return ctx;
}

